import * as RadixDialog from '@radix-ui/react-dialog';
import dynamic from 'next/dynamic';
import { useRef, useState, useEffect } from 'react';

import { useSession } from '@lichtblick/auth';
import { useTranslation } from '@lichtblick/i18next';
import { Article, Close, VideoCameraFront } from '@lichtblick/icons';
import { GetWorkOrderFilesMediaPayloadModel } from '@lichtblick/types';
import { deleteFile, downloadFileExtend } from '@lichtblick/utils';

import {
  DeleteDialog,
  GridViewContainer,
  Grid,
  CarouselContainerOverlay,
  CarouselDocContainer,
  IconButtonClose,
  PreviewWrapper,
  CarouselVideoContainer,
  DeleteContainer,
  CheckboxDeleteContainer,
} from './DocPreview.styles';
import { FileGridItem } from './FileGridItem';
import { docMimeType, imageMimeType, videoMimeType } from './utils';

import { Button } from '../Button';
import { Carousel } from '../Carousel';
import { ChatMessageImageRow } from '../ChatMessageImageRow';
import { Checkbox } from '../Checkbox';
import { Snackbar } from '../Snackbar';

const DocPreview = dynamic(() => import('./DocPreview').then((mod) => mod.DocPreview), {
  ssr: false,
});

export const FileGridView = ({
  allowMultiSelect = true,
  data,
  downloadLabel,
  folderId,
  isDownloading,
  isMonday,
  onDownload,
  onSelectionChange,
  refetch,
  showDownloadAction,
  workOrderId,
}: {
  allowMultiSelect?: boolean;
  data: GetWorkOrderFilesMediaPayloadModel[];
  downloadLabel?: string;
  folderId?: number;
  isDownloading?: boolean;
  isMonday?: boolean;
  onDownload?: () => void;
  onSelectionChange?: (ids: number[]) => void;
  refetch: () => void;
  showDownloadAction?: boolean;
  workOrderId: number;
}) => {
  const { data: session } = useSession();
  const { t } = useTranslation();
  const [itemToDelete, setItemToDelete] = useState<GetWorkOrderFilesMediaPayloadModel | null>(null);
  const [isShowingCarousel, setIsShowingCarousel] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const handleRef = useRef<{ goToSlide: (idx: number) => void }>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedFiles(new Set());
    onSelectionChange?.([]);
  }, [data, onSelectionChange]);

  const handleSelectFile = (fileId: number) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }

      onSelectionChange?.([...newSet].map((id) => Number(id)));

      return newSet;
    });
  };

  const handleSelectAll = () => {
    const nextSelection = selectedFiles.size === data.length ? new Set<number>() : new Set(data.map((item) => item.id));

    setSelectedFiles(nextSelection);
    onSelectionChange?.([...nextSelection].map((id) => Number(id)));
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) {
      return;
    }

    setIsDeleting(true);
    let deletedCount = 0;
    let failedCount = 0;

    for (const fileId of selectedFiles) {
      const file = data.find((f) => f.id === fileId);

      if (file) {
        try {
          await deleteFile(session, file.uri, folderId, file.id, workOrderId);
          deletedCount++;
        } catch (error) {
          failedCount++;
          // eslint-disable-next-line no-console
          console.error('Failed to delete file:', file.fileName, error);
        }
      }
    }

    setIsDeleting(false);
    setSelectedFiles(new Set());
    setShowBulkDeleteDialog(false);
    refetch();

    // Show result message via snackbar
    if (failedCount > 0) {
      setSnackbarMessage(t('pm:deleteBulkPartialSuccess', { deletedCount, failedCount }));
    } else {
      setSnackbarMessage(t('pm:deleteBulkSuccess', { deletedCount }));
    }

    setShowSnackbar(true);
  };

  const handleFileDownload = async (file: GetWorkOrderFilesMediaPayloadModel) => {
    if (!file.uri) {
      return;
    }

    await downloadFileExtend(file.uri, file.fileName ?? '');
  };

  const handleOpenInNewTab = async (file: GetWorkOrderFilesMediaPayloadModel) => {
    if (file.uri) {
      window.open(file.uri, '_blank', 'noopener,noreferrer');
    }
  };

  const renderByMimeType = (item: GetWorkOrderFilesMediaPayloadModel, isCarousel?: boolean, shouldFetch?: boolean) => {
    if (!item.mimeType) {
      return <Article />;
    }

    return (
      <PreviewWrapper>
        {imageMimeType.includes(item.mimeType) ? (
          <>
            {isCarousel ? (
              <CarouselDocContainer>
                <div
                  style={{
                    pointerEvents: 'auto',
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ChatMessageImageRow
                    expectedMaxWidth={900}
                    hasModal={false}
                    image={{
                      contentType: item.mimeType,
                      url: item.uri ?? '',
                    }}
                    isFileBrowser={true}
                    isInListView={false}
                  />
                </div>
              </CarouselDocContainer>
            ) : (
              <ChatMessageImageRow
                expectedMaxWidth={80}
                hasModal={false}
                image={{
                  contentType: item.mimeType,
                  url: item.uri ?? '',
                }}
                isFileBrowser={true}
                isInListView={false}
              />
            )}
          </>
        ) : videoMimeType.includes(item.mimeType) ? (
          <>
            {isCarousel ? (
              <CarouselVideoContainer>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  controls
                  playsInline
                  src={item.uri ?? ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </CarouselVideoContainer>
            ) : (
              <VideoCameraFront />
            )}
          </>
        ) : docMimeType.includes(item.mimeType) ? (
          <>
            {isCarousel ? (
              <CarouselDocContainer>
                <div style={{ pointerEvents: 'auto', height: '100%', width: '100%' }}>
                  <DocPreview
                    doc={{ contentType: item.mimeType, uri: item.uri ?? '', size: item.fileSize }}
                    hasModal={false}
                    isMonday={isMonday}
                    shouldFetch={shouldFetch}
                  />
                </div>
              </CarouselDocContainer>
            ) : (
              <Article />
            )}
          </>
        ) : (
          <Article />
        )}
      </PreviewWrapper>
    );
  };

  const handleOpenCarousel = (idx: number) => {
    setActiveSlideIndex(idx);
    setIsShowingCarousel(true);
    setTimeout(() => {
      handleRef.current?.goToSlide(idx);
    }, 0);
  };
  const handleSlideChange = (newIndex: number) => {
    setActiveSlideIndex(newIndex);
  };

  return (
    <GridViewContainer>
      {allowMultiSelect && selectedFiles.size > 0 && (
        <DeleteContainer>
          <CheckboxDeleteContainer>
            <Checkbox
              isChecked={selectedFiles.size === data.length && data.length > 0}
              label={`${selectedFiles.size} ${selectedFiles.size === 1 ? 'file' : 'files'} selected`}
              onChange={handleSelectAll}
            />
            {showDownloadAction && downloadLabel ? (
              <Button isLoading={isDownloading} label={downloadLabel} onClick={() => onDownload?.()} />
            ) : null}
            <Button
              isDisabled={isDeleting}
              label={isDeleting ? 'Deleting...' : 'Delete Selected'}
              onClick={() => setShowBulkDeleteDialog(true)}
              variant="rejection"
            />
          </CheckboxDeleteContainer>
        </DeleteContainer>
      )}

      <Grid>
        {data.map((item, idx) => (
          <FileGridItem
            allowMultiSelect={allowMultiSelect}
            isSelected={selectedFiles.has(item.id)}
            item={item}
            key={item.id}
            onClick={() => handleOpenCarousel(idx)}
            onDelete={(file) => setItemToDelete(file)}
            onDownload={(file) => handleFileDownload(file)}
            onOpenInTab={(file) => handleOpenInNewTab(file)}
            onSelectionChange={handleSelectFile}
            renderByMimeType={renderByMimeType}
          />
        ))}
      </Grid>

      <RadixDialog.Root open={isShowingCarousel}>
        <RadixDialog.Portal>
          <CarouselContainerOverlay
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsShowingCarousel(false);
              }
            }}
          >
            <Carousel
              onSlideChange={handleSlideChange}
              ref={handleRef}
              sliders={data.map((item, idx) => ({
                id: item.id,
                content: renderByMimeType(item, true, idx === activeSlideIndex),
              }))}
            />
            <RadixDialog.Close asChild>
              <IconButtonClose icon={<Close />} onClick={() => setIsShowingCarousel(false)} />
            </RadixDialog.Close>
          </CarouselContainerOverlay>
        </RadixDialog.Portal>
      </RadixDialog.Root>

      {/* Individual Delete Dialog */}
      <DeleteDialog
        acceptLabel={t('delete')}
        cancelLabel={t('cancel')}
        description={t('pm:deleteFile') ?? ''}
        isOpen={!!itemToDelete}
        onAccept={async () => {
          if (!itemToDelete?.id) {
            return;
          }

          await deleteFile(session, itemToDelete?.uri, folderId, itemToDelete?.id, workOrderId);
          refetch();
          setItemToDelete(null);
        }}
        onCancel={() => setItemToDelete(null)}
        onClose={() => setItemToDelete(null)}
        trigger={<></>}
      />

      {/* Bulk Delete Dialog */}
      <DeleteDialog
        acceptLabel={t('delete')}
        cancelLabel={t('cancel')}
        description={t('pm:deleteBulkFiles', { count: selectedFiles.size })}
        isOpen={showBulkDeleteDialog}
        onAccept={handleBulkDelete}
        onCancel={() => setShowBulkDeleteDialog(false)}
        onClose={() => setShowBulkDeleteDialog(false)}
        trigger={<></>}
      />

      <Snackbar
        color="black"
        isOpen={showSnackbar}
        message={snackbarMessage}
        setOpen={setShowSnackbar}
        title={t('pm:deleteBulkSuccessTitle')}
      />
    </GridViewContainer>
  );
};
